import { WorkerAutoAssignmentDefaultsView, WorkerAutoAssignmentPolicySettings } from '@nexelem/api';
import z from 'zod';

export const WorkerAssignmentRankingCriterion = {
  PartialOverlappingFte: 'partialOverlappingFte',
  HistoricalParallelFrequency: 'historicalParallelFrequency',
  CurrentMachineInRun: 'currentMachineInRun',
  CombinedExperience: 'combinedExperience',
  VariantExperience: 'variantExperience',
  MachineExperience: 'machineExperience',
  CurrentWorkload: 'currentWorkload',
} as const;

export type WorkerAssignmentRankingCriterion =
  (typeof WorkerAssignmentRankingCriterion)[keyof typeof WorkerAssignmentRankingCriterion];

export type WorkerAssignmentRankingCriterionWeight = {
  criterion: WorkerAssignmentRankingCriterion;
  enabled: boolean;
};

export type WorkerAssignmentRankingConfig = {
  criteria: WorkerAssignmentRankingCriterionWeight[];
};

export const DEFAULT_RANKING_CONFIG: WorkerAssignmentRankingConfig = {
  criteria: Object.values(WorkerAssignmentRankingCriterion).map((criterion) => {
    return { criterion, enabled: true };
  }),
};

// Mirrors backend defaults; used until GET auto-assign-workers-defaults responds.
export const FALLBACK_AUTO_ASSIGN_DEFAULTS: WorkerAutoAssignmentDefaultsView = {
  requireHistoricalAssignment: true,
  minAvailabilityCoveragePercent: 50,
  historicalAssignmentsLookbackMonths: 2,
  minOverlapsForPairPerMonth: 2,
};

// A policy config with every setting materialized (stored user overrides merged over server defaults).
export type ResolvedPolicyConfig = WorkerAutoAssignmentDefaultsView;

export const resolvePolicyConfig = (
  stored: WorkerAutoAssignmentPolicySettings | null,
  defaults: WorkerAutoAssignmentDefaultsView,
): ResolvedPolicyConfig => {
  return {
    requireHistoricalAssignment: stored?.requireHistoricalAssignment ?? defaults.requireHistoricalAssignment,
    minAvailabilityCoveragePercent: stored?.minAvailabilityCoveragePercent ?? defaults.minAvailabilityCoveragePercent,
    historicalAssignmentsLookbackMonths:
      stored?.historicalAssignmentsLookbackMonths ?? defaults.historicalAssignmentsLookbackMonths,
    minOverlapsForPairPerMonth: stored?.minOverlapsForPairPerMonth ?? defaults.minOverlapsForPairPerMonth,
  };
};

export const rankingCriterionLabelKeys = {
  [WorkerAssignmentRankingCriterion.PartialOverlappingFte]: 'ranking.criteria.partialOverlappingFteLabel',
  [WorkerAssignmentRankingCriterion.HistoricalParallelFrequency]: 'ranking.criteria.historicalParallelFrequencyLabel',
  [WorkerAssignmentRankingCriterion.CurrentMachineInRun]: 'ranking.criteria.currentMachineInRunLabel',
  [WorkerAssignmentRankingCriterion.CombinedExperience]: 'ranking.criteria.combinedExperienceLabel',
  [WorkerAssignmentRankingCriterion.VariantExperience]: 'ranking.criteria.variantExperienceLabel',
  [WorkerAssignmentRankingCriterion.MachineExperience]: 'ranking.criteria.machineExperienceLabel',
  [WorkerAssignmentRankingCriterion.CurrentWorkload]: 'ranking.criteria.currentWorkloadLabel',
} as const satisfies Record<WorkerAssignmentRankingCriterion, string>;

export const rankingCriterionHintKeys = {
  [WorkerAssignmentRankingCriterion.PartialOverlappingFte]: 'ranking.criteria.partialOverlappingFteHint',
  [WorkerAssignmentRankingCriterion.HistoricalParallelFrequency]: 'ranking.criteria.historicalParallelFrequencyHint',
  [WorkerAssignmentRankingCriterion.CurrentMachineInRun]: 'ranking.criteria.currentMachineInRunHint',
  [WorkerAssignmentRankingCriterion.CombinedExperience]: 'ranking.criteria.combinedExperienceHint',
  [WorkerAssignmentRankingCriterion.VariantExperience]: 'ranking.criteria.variantExperienceHint',
  [WorkerAssignmentRankingCriterion.MachineExperience]: 'ranking.criteria.machineExperienceHint',
  [WorkerAssignmentRankingCriterion.CurrentWorkload]: 'ranking.criteria.currentWorkloadHint',
} as const satisfies Record<WorkerAssignmentRankingCriterion, string>;

export const effectiveParallelPairThreshold = (perMonth: number, lookbackMonths: number) =>
  perMonth <= 0 ? 0 : Math.max(1, perMonth * Math.max(1, lookbackMonths));

export const historicalAssignmentsLookbackMonthsSchema = z.number().int().min(1);
export const minOverlapsForPairPerMonthSchema = z.number().int().min(0);
export const minAvailabilityCoveragePercentSchema = z.number().int().min(0).max(100);
